import { useContext, useEffect, useState } from "react";
import Switch from "../../../utils/components/Switch";
import { withLocalize } from "react-localize-redux";
import { Helmet } from "react-helmet-async";
import { useOutletContext } from "react-router";
import availableLanguages from "../../../utils/db/availableLanguages";
import { setLanguage } from "../../../utils/redux/app/appActions";
import { connect, useSelector } from "react-redux";
import { update } from "../../../utils/redux/auth/authActions";
import SelectBox from "../../../utils/components/SelectBox";
import Cookies from "universal-cookie";
import { RequestContext } from "../../../utils/context/RequestContext";
import PasskeySettings from "./PasskeySettings";

const AccountSettings = ({
  settings,
  setLanguage,
  translate,
  update: userUpdate,
}) => {
  // eslint-disable-next-line no-unused-vars
  const [twoFactorAuthentication, setTwoFactorAuthentication] = useState(true);
  const [accountSettings, setAccountSettings] = useState({
    instant_daily: null,
    lang: "",
    notification_email: true,
    twoFactorAuthentication: false,
  });
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedInstantDaily, setSelectedInstantDaily] = useState(null);

  // eslint-disable-next-line no-unused-vars
  const { setBreadcrumbs, userInfo, updateUserData } = useOutletContext();

  const { setRequestLoading } = useContext(RequestContext);
  const { language } = useSelector((state) => state.app.settings);

  const cookies = new Cookies();

  useEffect(() => {
    if (userInfo) {
      setBreadcrumbs([
        {
          id: 0,
          title: "page_titles.account",
          route: "auth.account",
        },
        { id: 1, customTitle: userInfo?.name || "" },
        {
          id: 2,
          title: "page_titles.account-settings",
        },
      ]);

      const accountSettings = {
        instant_daily: {
          value: userInfo?.instant_daily,
          label: userInfo?.instant_daily
            ? translate(`account.${userInfo?.instant_daily}`)
            : translate("account.instant"),
        },
        lang: userInfo?.lang,
        notification_email: userInfo?.notification_email ? true : false,
        twoFactorAuthentication: false,
      };

      setAccountSettings(accountSettings);
      setSelectedInstantDaily({
        value: userInfo?.instant_daily,
        label: userInfo?.instant_daily
          ? translate(`account.${userInfo?.instant_daily}`)
          : translate("account.instant"),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  useEffect(() => {
    setSelectedInstantDaily({
      value: userInfo?.instant_daily,
      label: userInfo?.instant_daily
        ? translate(`account.${userInfo?.instant_daily}`)
        : translate("account.instant"),
    });
  }, [language, translate, userInfo?.instant_daily]);

  useEffect(() => {
    if (settings.language && !selectedLanguage) {
      const lang = availableLanguages.find(
        (item) => item.code === settings.language
      );

      setSelectedLanguage({ value: lang.code, label: lang.name });
    }
  }, [selectedLanguage, settings]);

  useEffect(() => {
    const selectedLang = cookies.get("language") || settings.language;

    const lang = availableLanguages.find((item) => item.code === selectedLang);

    if (lang) setSelectedLanguage({ value: lang.code, label: lang.name });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      selectedInstantDaily?.value &&
      selectedInstantDaily?.value !== userInfo?.instant_daily
    ) {
      handleUpdateAccount({
        target: { name: "instant_daily", value: selectedInstantDaily?.value },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstantDaily]);

  useEffect(() => {
    handleUpdateLanguage(selectedLanguage?.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const handleUpdateLanguage = (lang) => {
    if (
      lang &&
      lang !== settings.language &&
      availableLanguages.find((item) => item.code === lang)
    ) {
      setRequestLoading(true);
      updateUserData({ lang }).then(() => {
        userUpdate({ lang });
        setLanguage(lang);

        setAccountSettings({
          ...accountSettings,
          instant_daily: {
            value: userInfo?.instant_daily,
            label: userInfo?.instant_daily
              ? translate(`account.${userInfo?.instant_daily}`)
              : translate("account.instant"),
          },
          lang,
        });
      });
    }
  };

  const handleUpdateAccount = (e) => {
    setRequestLoading(true);
    updateUserData({
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }).then(() => {
      setAccountSettings({
        ...accountSettings,
        [e.target.name]:
          e.target.type === "checkbox" ? e.target.checked : e.target.value,
      });
    });
  };

  return (
    <div className="container-fluid pb-5">
      <Helmet>
        <title>{translate("page_titles.account-settings")}</title>
      </Helmet>
      <div className="row">
        <div className="col-12 border-bottom border-dark py-4">
          <p className="fs-4 fw-bold pt-2 mb-0">
            {translate("account.settings.titles.account-settings")}
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col-12 mt-2">
          <p className="fs-5 fw-bold pt-3 pb-1">
            {translate("account.settings.titles.mail-notification-settings")}
          </p>
        </div>
        <div className="col-12 mt-2">
          <p className="fw-bold">
            {translate("account.settings.titles.enable-mail-notifications")}
          </p>
        </div>
        <div className="col-12 d-flex">
          <p className="pe-4">
            {translate(
              "account.settings.descriptions.enable-mail-notifications"
            )}
          </p>
          <div className="ms-auto">
            <Switch
              checked={accountSettings.notification_email}
              onChange={() =>
                handleUpdateAccount({
                  target: {
                    name: "notification_email",
                    type: "checkbox",
                    checked: !accountSettings.notification_email,
                  },
                })
              }
            />
          </div>
        </div>
        <div className="col-12 mt-2">
          <p className="fw-bold mb-0 mb-2">
            {translate("account.settings.titles.notification-preference")}
          </p>
        </div>
        <div className="col-12 d-flex align-items-center">
          <p className="pe-4">
            {translate("account.settings.descriptions.notification-preference")}
          </p>
          <div className="ms-auto w-25">
            <SelectBox
              options={[
                { value: "instant", label: translate("account.instant") },
                { value: "daily", label: translate("account.daily") },
              ]}
              placeholder="Select"
              value={selectedInstantDaily}
              onChange={(value) => setSelectedInstantDaily(value)}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 mt-2">
          <p className="fs-5 fw-bold pt-3">
            {translate("account.settings.titles.language-settings")}
          </p>
        </div>
        <div className="col-12 mt-2">
          <p className="fw-bold mb-0 mb-1">
            {translate("account.settings.titles.language-preference")}
          </p>
        </div>
        <div className="col-12 d-flex align-items-center">
          <p className="pe-4">
            {translate("account.settings.descriptions.language-preference")}
          </p>
          <div className="ms-auto w-25">
            <SelectBox
              options={availableLanguages.map((lang) => ({
                value: lang.code,
                label: lang.name,
              }))}
              onChange={(item) => setSelectedLanguage(item)}
              value={selectedLanguage}
            />
          </div>
        </div>
      </div>
      <PasskeySettings translate={translate} />
      {/* <div className="row">
        <div className="col-12 mt-2">
          <p className="fs-5 fw-bold pt-3 pb-1">
            Two-Factor Authentication Settings
          </p>
        </div>
        <div className="col-12 mt-2">
          <p className="fw-bold">Enable Two-Factor Authentications</p>
        </div>
        <div className="col-12 d-flex">
          <p className="pe-4 pt-2">
            Enabling Two-Factor Authentication allows you to make your account
            more secure
          </p>
          <div className="ms-auto">
            <Switch
              checked={twoFactorAuthentication}
              onChange={() =>
                setTwoFactorAuthentication(!twoFactorAuthentication)
              }
            />
          </div>
        </div>
      </div> */}
    </div>
  );
};

const mapStateToProps = (state) => ({
  settings: state.app.settings,
  user: state.auth.user,
});

const mapDispatchToProps = (dispatch) => ({
  setLanguage: (lang) => dispatch(setLanguage(lang)),
  update: (user) => dispatch(update(user)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withLocalize(AccountSettings));
